import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Track, Artist } from '../types/artist';
import { trackApi } from '../services/trackApi';
import { artistApi } from '../services/artistApi';
import { API_BASE_URL } from '../config/api';
import { ArtistOverview } from './shared/ArtistOverview';
import { TrackCard } from './shared/TrackCard';
import { TransportControl } from './shared/TransportControl';
import { Feedback } from './shared/Feedback';
import './TrackManager.css';

interface TrackManagerProps {
  artistId?: number;
  userId?: string; // User ID to fetch tracks efficiently
  showFeedback?: boolean;
  refreshTrigger?: number; // Add trigger for external refresh
  selectedTrackPublicId?: string; // Public ID of track to auto-select
}

const TrackManager: React.FC<TrackManagerProps> = ({ artistId, userId, showFeedback = true, refreshTrigger, selectedTrackPublicId }) => {
  const [tracks, setTracks] = useState<Track[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTrack, setSelectedTrack] = useState<Track | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [trackToDelete, setTrackToDelete] = useState<Track | null>(null);
  const [trackToSetReview, setTrackToSetReview] = useState<Track | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSettingReview, setIsSettingReview] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [showArtistModal, setShowArtistModal] = useState(false);
  const [fullArtist, setFullArtist] = useState<Artist | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [defaultArtist, setDefaultArtist] = useState<Artist | null>(null);
  const [loadingDefaultArtist, setLoadingDefaultArtist] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  const loadTracks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use user-specific endpoint if userId is provided for better performance
      const tracksData = userId 
        ? await trackApi.getTracksByUserId(userId)
        : await trackApi.getAllTracks();
        
      setTracks(tracksData);
    } catch (err) {
      console.error('Failed to load tracks:', err);
      setError(err instanceof Error ? err.message : 'Failed to load tracks');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadTracks();
  }, [loadTracks, artistId, refreshTrigger]); // Add refreshTrigger dependency

  // Auto-select track when selectedTrackPublicId is provided
  useEffect(() => {
    if (selectedTrackPublicId && tracks.length > 0) {
      const trackToSelect = tracks.find(track => track.publicId === selectedTrackPublicId);
      if (trackToSelect) {
        setSelectedTrack(trackToSelect);
        // Scroll to the track in the list
        setTimeout(() => {
          const trackElement = document.getElementById(`track-${trackToSelect.id}`);
          if (trackElement) {
            trackElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
    }
  }, [selectedTrackPublicId, tracks]);

  const loadDefaultArtist = useCallback(async () => {
    // Find the first artist from the tracks, or use the provided artistId
    const targetArtistId = artistId || tracks[0]?.artistId;
    if (!targetArtistId) return;
    
    setLoadingDefaultArtist(true);
    try {
      const artist = await artistApi.getArtistById(targetArtistId);
      setDefaultArtist(artist);
    } catch (error) {
      console.error('Failed to fetch default artist details:', error);
      // Fallback to basic artist info from first track
      setDefaultArtist(tracks[0]?.artist || null);
    } finally {
      setLoadingDefaultArtist(false);
    }
  }, [artistId, tracks]);

  // Load default artist for the overview when tracks are loaded
  useEffect(() => {
    if (tracks.length > 0 && !defaultArtist && !loadingDefaultArtist) {
      loadDefaultArtist();
    }
  }, [tracks, defaultArtist, loadingDefaultArtist, loadDefaultArtist]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleDurationChange = () => {
      // Only update duration if metadata has already been loaded
      if (audio.readyState >= 1) { // HAVE_METADATA or higher
        const audioDuration = audio.duration;
        if (isFinite(audioDuration) && !isNaN(audioDuration) && audioDuration > 0) {
          setDuration(audioDuration);
        } else {
          setDuration(0);
        }
      }
    };
    
    const handleLoadedMetadata = () => {
      // Reset current time when new metadata loads
      setCurrentTime(0);
      audio.currentTime = 0;
      
      // Set duration from metadata
      const audioDuration = audio.duration;
      if (isFinite(audioDuration) && !isNaN(audioDuration) && audioDuration > 0) {
        setDuration(audioDuration);
      } else {
        setDuration(0);
      }
      
      // Clear any previous errors since metadata loaded successfully
      setError(null);
      
      console.log('Audio metadata loaded:', {
        duration: audioDuration,
        readyState: audio.readyState,
        networkState: audio.networkState
      });
    };
    const handleEnded = () => setIsPlaying(false);
    const handleError = (e: Event) => {
      console.error('Audio playback error:', e);
      setError('Failed to load audio file');
      setIsPlaying(false);
      setDuration(0);
      setCurrentTime(0);
    };
    const handleCanPlay = () => {
      setError(null); // Clear any previous errors
    };
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('error', handleError);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, [selectedTrack]);

  // Update audio volume when volume or mute state changes
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  const handleTrackSelect = (track: Track) => {
    if (selectedTrack?.id === track.id) {
      // Deselect if same track
      setSelectedTrack(null);
      setIsPlaying(false);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
      // Reset metadata when deselecting
      setCurrentTime(0);
      setDuration(0);
      return;
    }
    
    setSelectedTrack(track);
    setIsPlaying(false);
    
    // Reset metadata immediately when selecting new track
    setCurrentTime(0);
    setDuration(0);
    setError(null);
    
    // Set the audio source if track has an audio URL
    console.log('Track audioUrl:', track.audioUrl);
    console.log('Audio ref current:', audioRef.current);
    console.log('Audio URL exists check:', !!track.audioUrl);
    console.log('Audio ref exists check:', !!audioRef.current);
    
    // Use setTimeout to ensure audio element is rendered
    setTimeout(() => {
      if (track.id && audioRef.current) {
        // Primary approach: use API endpoint for audio streaming
        const streamUrl = trackApi.getAudioStreamUrl(track.id);
        console.log('Using stream URL:', streamUrl);
        audioRef.current.src = streamUrl;
        audioRef.current.load();
      } else if (track.audioUrl && audioRef.current) {
        // Fallback: try direct URL approach
        const fullAudioUrl = track.audioUrl.startsWith('http') 
          ? track.audioUrl 
          : `${API_BASE_URL}${track.audioUrl}`;
        console.log('Fallback: setting audio source to:', fullAudioUrl);
        audioRef.current.src = fullAudioUrl;
        audioRef.current.load();
      } else {
        console.warn('No audio available for track:', track);
        console.warn('Track ID:', track.id);
        console.warn('audioUrl value:', JSON.stringify(track.audioUrl));
        console.warn('audioRef.current:', audioRef.current);
      }
    }, 100);
  };

  const handlePlayPause = () => {
    const audio = audioRef.current;
    if (!audio || !selectedTrack) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play().catch(err => {
        console.error('Failed to play audio:', err);
        setError('Failed to play audio file. The file may still be processing or there was a playback error.');
      });
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;

    const newTime = parseFloat(e.target.value);
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (isMuted && newVolume > 0) {
      setIsMuted(false);
    }
  };

  const handleMuteToggle = () => {
    setIsMuted(!isMuted);
  };

  const handleDeleteTrack = async (track: Track) => {
    if (!track.id) return;

    setIsDeleting(true);
    try {
      await trackApi.deleteTrack(track.id);
      
      // Update the tracks list by removing the deleted track
      setTracks(prevTracks => prevTracks.filter(t => t.id !== track.id));
      
      // If the deleted track was selected, clear the selection
      if (selectedTrack?.id === track.id) {
        setSelectedTrack(null);
        setIsPlaying(false);
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.src = '';
        }
      }
      
      setTrackToDelete(null);
    } catch (err) {
      console.error('Failed to delete track:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete track');
    } finally {
      setIsDeleting(false);
    }
  };

  const confirmDelete = (track: Track) => {
    setTrackToDelete(track);
  };

  const cancelDelete = () => {
    setTrackToDelete(null);
  };

  const handleSetToReview = async (track: Track) => {
    if (!track.id) return;

    setIsSettingReview(true);
    try {
      await trackApi.updateTrack(track.id, {
        title: track.title,
        artistId: track.artistId || 0,
        genre: track.genre,
        underReview: true
      });
      
      // Reload tracks to get the latest state from server
      // This ensures only one track per artist shows as under review
      await loadTracks();
      
      setTrackToSetReview(null);
    } catch (err) {
      console.error('Failed to set track to review:', err);
      setError(err instanceof Error ? err.message : 'Failed to set track to review');
    } finally {
      setIsSettingReview(false);
    }
  };

  const confirmSetToReview = (track: Track) => {
    setTrackToSetReview(track);
  };

  const cancelSetToReview = () => {
    setTrackToSetReview(null);
  };

  const generateShareUrl = (track: Track): string => {
    if (!track.publicId) return '';
    const baseUrl = window.location.origin;
    return `${baseUrl}/request-feedback/${track.publicId}`;
  };

  const handleShare = async (track: Track, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!track.publicId) {
      setError('Cannot share track: no public ID available');
      return;
    }

    const url = generateShareUrl(track);
    const title = `Give feedback on "${track.title}"`;
    const text = `Check out this track and give feedback: ${track.title}`;

    // Check if we're on mobile and Web Share API is available
    if (navigator.share && /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
      try {
        await navigator.share({
          title,
          text,
          url
        });
      } catch (err) {
        // User cancelled or share failed, fallback to modal
        if (err instanceof Error && err.name !== 'AbortError') {
          console.error('Share failed:', err);
          showShareModalForTrack(track);
        }
      }
    } else {
      // Desktop: show modal
      showShareModalForTrack(track);
    }
  };

  const showShareModalForTrack = (track: Track) => {
    const url = generateShareUrl(track);
    setShareUrl(url);
    setShowShareModal(true);
    setCopySuccess(false);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const closeShareModal = () => {
    setShowShareModal(false);
    setShareUrl('');
    setCopySuccess(false);
  };

  const handleArtistModalOpen = async (artistId?: number) => {
    if (!artistId) return;
    
    setShowArtistModal(true);
    
    try {
      const artist = await artistApi.getArtistById(artistId);
      setFullArtist(artist);
    } catch (error) {
      console.error('Failed to fetch artist details:', error);
      // Keep the modal open but show basic info from track
      setFullArtist(selectedTrack?.artist || null);
    }
  };

  const closeArtistModal = () => {
    setShowArtistModal(false);
    setFullArtist(null);
  };

  if (loading) {
    return (
      <div className="track-manager">
        <div className="loading">Loading tracks...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="track-manager">
        <div className="error">
          <p>Error: {error}</p>
          <button className="retry-button" onClick={loadTracks}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="track-manager">
      {/* Main Content Layout */}
      <div className="main-content-layout">
        {/* Left Column - Track List */}
        <div className="left-column">
          <div className="track-list">
            <h3>Your Tracks</h3>
            {tracks.filter(track => !artistId || track.artistId === artistId).length === 0 ? (
              <div className="no-tracks">
                <p>No tracks uploaded yet.</p>
                <p>Upload your first track to get feedback!</p>
              </div>
            ) : (
              <div className="tracks-list">
                {tracks
                  .filter(track => !artistId || track.artistId === artistId)
                  .map(track => (
                  <TrackCard
                    key={track.id}
                    track={track}
                    isSelected={selectedTrack?.id === track.id}
                    isPlaying={isPlaying}
                    onClick={handleTrackSelect}
                    onPlayPause={handlePlayPause}
                    showArtist={false} // Don't show artist in TrackManager since it's user's own tracks
                    showFeedbackSummary={showFeedback}
                    showShareButton={true}
                    showDeleteButton={true}
                    showReviewStatus={true}
                    onShare={handleShare}
                    onDelete={(track, e) => {
                      e.stopPropagation();
                      confirmDelete(track);
                    }}
                    onSetToReview={(track, e) => {
                      e.stopPropagation();
                      confirmSetToReview(track);
                    }}
                    className=""
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Audio Player and Feedback */}
        <div className="right-column">
          {selectedTrack ? (
            <>
              {/* Audio Player */}
              {(selectedTrack.audioUrl || selectedTrack.id) && (
                <TransportControl
                  track={selectedTrack}
                  isPlaying={isPlaying}
                  currentTime={currentTime}
                  duration={duration}
                  volume={volume}
                  isMuted={isMuted}
                  onPlayPause={handlePlayPause}
                  onSeek={handleSeek}
                  onVolumeChange={handleVolumeChange}
                  onMuteToggle={handleMuteToggle}
                  showCloseButton={true}
                  onClose={() => setSelectedTrack(null)}
                  onArtistClick={handleArtistModalOpen}
                  audioRef={audioRef}
                />
              )}

              {/* Track Feedback */}
              {showFeedback && selectedTrack.feedback && selectedTrack.feedback.length > 0 && (
                <div className="track-feedback">
                  <h4>Feedback for "{selectedTrack.title}"</h4>
                  <div className="feedback-list">
                    {selectedTrack.feedback.map(feedback => (
                      <Feedback 
                        key={feedback.id}
                        feedback={feedback}
                        variant="list"
                        showAuthor={true}
                        showThreadButton={true}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div>
              {loadingDefaultArtist ? (
                <div className="loading-artist">
                  <p>Loading artist information...</p>
                </div>
              ) : defaultArtist ? (
                <ArtistOverview 
                  artist={defaultArtist}
                  showAsModal={false}
                />
              ) : (
                <div className="no-track-selected">
                  <h3>Select a track to play and view details</h3>
                  <p>Choose a track from the list on the left to start listening and view any feedback received.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <div className="share-modal-overlay" onClick={closeShareModal}>
          <div className="share-modal" onClick={(e) => e.stopPropagation()}>
            <div className="share-modal-header">
              <h3>Share Track for Feedback</h3>
              <button 
                className="modal-close-button"
                onClick={closeShareModal}
              >
                ✕
              </button>
            </div>
            <div className="share-modal-content">
              <p>Share this link to let others give feedback on your track:</p>
              <div className="share-url-container">
                <input
                  type="text"
                  value={shareUrl}
                  readOnly
                  className="share-url-input"
                />
                <button
                  className={`copy-button ${copySuccess ? 'success' : ''}`}
                  onClick={copyToClipboard}
                >
                  {copySuccess ? '✓ Copied!' : 'Copy'}
                </button>
              </div>
              {copySuccess && (
                <p className="copy-success-message">
                  Link copied to clipboard!
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Set to Review Confirmation Dialog */}
      {trackToSetReview && (
        <div className="delete-dialog-overlay">
          <div className="delete-dialog">
            <h3>Set Track to Review</h3>
            <p>Are you sure you want to set "{trackToSetReview.title}" for review?</p>
            <p className="info-text">This will make your track available for community feedback.</p>
            <div className="dialog-actions">
              <button 
                className="cancel-button" 
                onClick={cancelSetToReview}
                disabled={isSettingReview}
              >
                Cancel
              </button>
              <button 
                className="confirm-button" 
                onClick={() => handleSetToReview(trackToSetReview)}
                disabled={isSettingReview}
              >
                {isSettingReview ? 'Setting...' : 'Set to Review'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {trackToDelete && (
        <div className="delete-dialog-overlay">
          <div className="delete-dialog">
            <h3>Delete Track</h3>
            <p>Are you sure you want to delete "{trackToDelete.title}"?</p>
            <p className="warning-text">This action cannot be undone. All feedback for this track will also be lost.</p>
            <div className="dialog-actions">
              <button 
                className="cancel-button" 
                onClick={cancelDelete}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button 
                className="delete-confirm-button" 
                onClick={() => handleDeleteTrack(trackToDelete)}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete Track'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Artist Overview Modal */}
      {showArtistModal && (fullArtist || selectedTrack?.artist) && (
        <ArtistOverview 
          artist={fullArtist || selectedTrack!.artist!}
          showAsModal={true}
          onClose={closeArtistModal}
        />
      )}
    </div>
  );
};

export default TrackManager;